import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  FormControlLabel,
  Grid,
  SelectChangeEvent,
  TextField
} from "@mui/material";
import {
  StyledContainer,
  StyledHeader,
  StyledTextField,
  StyledStack,
} from "./Profile.styles";
import Button from "components/shared/Button/Button";
import API from "api";
import { GlobalContext } from "context/Provider";
import Session from "helpers/session";
import { useTranslation } from "react-i18next";
import { AddEncounterEvent } from "helpers/variables";
import { EncounterTimerDisplay } from "components/shared/EncounterTimer/EncounterTimer";

interface ProfileProps {
  type: "coordinator" | "members";
  action: "create" | "edit";
  isAvatar: boolean;
  memberId: string;
}

export default function Profile({type, action, isAvatar, memberId}: ProfileProps) {
  const { t } = useTranslation();
  const {
    member,
    memberDispatch,
    config,
    provider,
    encounterTimer
  } = useContext(GlobalContext);
  const availablePrograms = config?.regOptions.programs || [];
  const availableGroups = config?.regOptions.groups || [];
  const [isDisabled, setChanged] = useState<boolean>(true);
  const [showSuccess, setSuccess] = useState<string>("");
  const [memberChanges, setMemberChanges] = useState(
    !isAvatar
      ? {
          id: member?.current.id || "",
          email: member?.current.email || "",
          externId: member?.current.externId || "",
          firstName:
            type === "coordinator"
              ? member?.current.username.split(" ")[0]
              : member?.current.firstName || "",
          lastName:
            type === "coordinator"
              ? member?.current.username.split(" ")[1]
              : member?.current.lastName || "",
          username: member?.current.username,
          program: member?.current.program,
          phone: member?.current.phone,
          groups: member?.current.groups || [],
          site: member?.current.site,
          birthDate: member?.current.birthDate,
          gender: member?.current.gender,
          language: member?.current.language,
          condition: member?.current.condition,
          height: member?.current.height,
          weight: member?.current.weight,
          status: member?.current.status,
          units: member?.current.units,
          locked: member?.current.locked,
          latestReading: member?.current.latestReading,
          mostRecentReading: member?.current.mostRecentReading
        }
      : {
          email: provider?.loggedInClient.email || "",
          firstName: !provider?.loggedInClient.username.includes(" ")
            ? provider?.loggedInClient.username
            : provider?.loggedInClient.username.split(" ")[0] || "",
          lastName: !provider?.loggedInClient.username.includes(" ")
            ? ""
            : provider?.loggedInClient.username.split(" ")[1] || "",
          site: provider?.loggedInClient.site,
          groups: provider?.loggedInClient.groups || []
        }
  );
  const updateMember = () => {
    let changes = memberChanges;
    changes.username = `${memberChanges.firstName} ${memberChanges.lastName}`;
    if (type === "members") {
      changes.phone = memberChanges.phone
        ?.replaceAll(" ", "")
        .replaceAll("(", "")
        .replaceAll(")", "")
        .replaceAll("-", "")
        .slice(0, 10);
      if (changes.phone !== "") {
        if (isNaN(parseInt(changes.phone!)) || changes.phone?.length !== 10) {
          alert("Not a valid phone number");
          changes.phone = "";
        } else {
          changes.phone = [
            "(",
            memberChanges.phone?.slice(0, 3),
            ")",
            "-",
            memberChanges.phone?.slice(3, 6),
            "-",
            memberChanges.phone?.slice(6, 10)
          ].join("");
        }
      }
    }

    let userCall = API.kore[type].upsert(
      action === "create" ? "create" : "edit",
      changes,
      Session.crackCookieToken("coordinator") ||
        Session.crackCookieToken("provider"),
      member?.current.id
    );
    setSuccess("success");
    // if(type === 'coordinator'){
    //     userCall = API.kore.coordinator.upsert(action === 'create' ? 'create' : 'edit', changes, Session.crackCookieToken('coordinator') || Session.crackCookieToken('provider'), member?.current.id)
    // }
    userCall.then((member) => {
      setChanged(true);
      if (type === "members") {
        memberDispatch({
          type: "CHANGE_TARGET_MEMBER",
          payload: {
            id: changes.id,
            email: changes.email,
            externId: changes.externId,
            firstName: changes.firstName,
            lastName: changes.lastName,
            username: changes.username,
            program: changes.program,
            phone: changes.phone,
            groups: changes.groups,
            site: changes.site,
            birthDate: changes.birthDate,
            gender: changes.gender,
            language: changes.language,
            condition: changes.condition,
            height: changes.height,
            weight: changes.weight,
            status: changes.status,
            units: changes.units,
            locked: changes.locked,
            latestReading: changes.latestReading,
            mostRecentReading: changes.mostRecentReading
          }
        });
      }
    });
    const encounterEvent = AddEncounterEvent(
      "profile change",
      "Profile updated"
    );
    const duration = encounterTimer?.current?.duration ?? 0;
    // encounterDispatch({type: 'CHANGE_TARGET_ENCOUNTER', payload: encounters?.current.concat(encounterEvent)})
    Session.updateSessionEncounter(
      window.sessionStorage.getItem("encounter") || "",
      encounterEvent,
      duration.toString() || ""
    );
  };
  const handleMemberChange = (event: any) => {
    setChanged(false);
    setSuccess("");
    setMemberChanges({
      ...memberChanges,
      [event.target.id]: event.target.value
    });
  };

  const handleSelection = (event: any, value: any) => {
    setChanged(false);
    setSuccess("");
    const dropKey = event.target.id.split("-")[0];
    const selectValue = dropKey === "program" ? value : [value];
    setMemberChanges({ ...memberChanges, [dropKey]: selectValue });
  };

  return (
    <StyledContainer>
      <StyledHeader>
        <h2>
          {type === "members" ? "Member" : "Coordinator"} {t(`u-profile`)}
        </h2>
      </StyledHeader>
      {!isAvatar && <EncounterTimerDisplay/>}
      <Grid container>
        <Grid item xs={2} md={1.3} />
        <Grid item sm={11} md={8}>
          <StyledStack spacing={1}>
            <br />
            <FormControlLabel
              label={t("u-email")}
              control={
                <StyledTextField
                  id="email"
                  disabled={isAvatar ? true : false}
                  placeholder={t("u-email")}
                  onChange={handleMemberChange}
                  value={memberChanges.email}
                />
              }
              labelPlacement="start"
            />
            <FormControlLabel
              label={t("u-first-name")}
              control={
                <StyledTextField
                  id="firstName"
                  disabled={isAvatar ? true : false}
                  placeholder={t("u-first-name")}
                  onChange={handleMemberChange}
                  value={memberChanges.firstName}
                />
              }
              labelPlacement="start"
            />
            <FormControlLabel
              label={t("u-last-name")}
              control={
                <StyledTextField
                  id="lastName"
                  disabled={isAvatar ? true : false}
                  placeholder={t("u-last-name")}
                  onChange={handleMemberChange}
                  value={memberChanges.lastName}
                />
              }
              labelPlacement="start"
            />
            {type === "members" && (
              <FormControlLabel
                label={t("u-phone")}
                control={
                  <StyledTextField
                    id="phone"
                    placeholder={t("u-phone")}
                    onChange={handleMemberChange}
                    value={memberChanges?.phone}
                  />
                }
                labelPlacement="start"
              />
            )}
            <FormControlLabel
              label={t("u-site")}
              control={
                <StyledTextField
                  id="site"
                  disabled={isAvatar ? true : false}
                  placeholder={t("u-site")}
                  onChange={handleMemberChange}
                  value={memberChanges.site}
                />
              }
              labelPlacement="start"
            />
            {type === "members" && (
              <FormControlLabel
                label="MRN"
                control={
                  <StyledTextField
                    id="externId"
                    placeholder="MRN"
                    onChange={handleMemberChange}
                    value={memberChanges.externId}
                  />
                }
                labelPlacement="start"
              />
            )}
            <FormControlLabel
              label={t("u-group")}
              disabled={isAvatar ? true : false}
              control={
                <Autocomplete
                  id="groups"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="groups"
                      placeholder="group-1"
                      sx={{ width: "86%", float: "right" }}
                    />
                  )}
                  options={availableGroups}
                  onChange={handleSelection}
                  value={memberChanges.groups[0]}
                  fullWidth
                />
              }
              // control={<StyledTextField id="group" placeholder={t('u-group')} onChange={handleMemberChange} value={memberChanges.group}/>}
              labelPlacement="start"
            />
            {type === "members" && (
              <FormControlLabel
                label={t("u-program")}
                control={
                  <Autocomplete
                    id="program"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="program"
                        placeholder="Cardiac"
                        sx={{ width: "88%", float: "right" }}
                      />
                    )}
                    options={availablePrograms.map((name) => {
                      return name.name;
                    })}
                    onChange={handleSelection}
                    value={memberChanges.program}
                    fullWidth
                  />
                }
                // control={<StyledTextField id="group" placeholder={t('u-group')} onChange={handleMemberChange} value={memberChanges.group}/>}
                labelPlacement="start"
              />
            )}
            {/* <FormTagLabel */}
            {/*<FormControlLabel*/}
            {/*    label={t('u-program')}*/}
            {/*    control={<StyledTextField id="program" placeholder={t('u-program')} onChange={handleMemberChange} value={memberChanges.program}/>}*/}
            {/*    // control={<StyledTagField*/}
            {/*    //     selectedTags={handleSelecetedTags}*/}
            {/*    //     variant="outlined"*/}
            {/*    //     id="tags"*/}
            {/*    //     name="tags"*/}
            {/*    //     placeholder="add Tags"*/}
            {/*    // />}*/}
            {/*    labelPlacement="start"*/}
            {/*/>*/}
          </StyledStack>
          <br />
          {!isAvatar && (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="success"
                onClick={updateMember}
                disabled={isDisabled}
                width="90%"
              >
                {t("u-save")}
              </Button>
            </div>
          )}
          {showSuccess === "success" ? (
            <Alert severity="success">
              Your configuration changes have been saved
            </Alert>
          ) : (
            ""
          )}
        </Grid>
      </Grid>
    </StyledContainer>
  );
}
